import { useEffect, useMemo, useState } from "react";
import {
  FiCheck,
  FiFilter,
  FiLoader,
  FiPackage,
  FiPlus,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../../services/api";

const emptyFilters = {
  query: "",
  brandId: "",
  categoryId: "",
  subCategoryId: "",
  seasonId: "",
  colorId: "",
  sizeId: "",
  gstRuleGroup: "",
};

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const estimatedGstRate = (variant) => {
  const value = Number(variant.purchasePrice || 0);
  const matchingBand = (variant.gstBands || []).find((band) => {
    const minimum =
      band.minimumItemValue === null || band.minimumItemValue === undefined
        ? null
        : Number(band.minimumItemValue);
    const maximum =
      band.maximumItemValue === null || band.maximumItemValue === undefined
        ? null
        : Number(band.maximumItemValue);

    return (minimum === null || value >= minimum) &&
      (maximum === null || value <= maximum);
  });

  return Number(matchingBand?.rate ?? 0);
};

export default function PurchaseOrderProductFilterModal({
  open,
  onClose,
  supplierId,
  accessToken,
  onAddItems,
}) {
  const [filters, setFilters] = useState(emptyFilters);
  const [filterOptions, setFilterOptions] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedVariantIds, setSelectedVariantIds] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !accessToken) return;

    const loadFilters = async () => {
      try {
        setIsLoadingFilters(true);
        setError("");
        setFilters(emptyFilters);
        setResults([]);
        setSelectedVariantIds([]);

        const response = await apiRequest("/purchases/catalog/filters", {
          accessToken,
        });

        setFilterOptions(response || {});
      } catch (requestError) {
        setError(
          requestError.message || "Unable to load product search filters.",
        );
      } finally {
        setIsLoadingFilters(false);
      }
    };

    loadFilters();
  }, [open, accessToken]);

  const subCategories = useMemo(
    () =>
      (filterOptions?.subCategories || []).filter(
        (subCategory) =>
          !filters.categoryId || subCategory.categoryId === filters.categoryId,
      ),
    [filterOptions, filters.categoryId],
  );

  const updateFilter = (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value,
      ...(field === "categoryId" ? { subCategoryId: "" } : {}),
    }));
  };

  const searchProducts = async () => {
    if (!supplierId) {
      setError("Select a supplier before searching for products.");
      return;
    }

    try {
      setIsSearching(true);
      setError("");

      const params = new URLSearchParams({
        supplierId,
        take: "100",
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        }
      });

      const response = await apiRequest(
        `/purchases/catalog?${params.toString()}`,
        { accessToken },
      );

      setResults(Array.isArray(response) ? response : []);
      setSelectedVariantIds([]);
    } catch (requestError) {
      setError(requestError.message || "Unable to search products.");
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const toggleVariant = (variantId) => {
    setSelectedVariantIds((previous) =>
      previous.includes(variantId)
        ? previous.filter((id) => id !== variantId)
        : [...previous, variantId],
    );
  };

  const allResultsSelected =
    results.length > 0 &&
    results.every((variant) => selectedVariantIds.includes(variant.id));

  const toggleAllResults = () => {
    setSelectedVariantIds(
      allResultsSelected ? [] : results.map((variant) => variant.id),
    );
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setResults([]);
    setSelectedVariantIds([]);
    setError("");
  };

  const addSelectedProducts = () => {
    const selected = results.filter((variant) =>
      selectedVariantIds.includes(variant.id),
    );

    if (!selected.length) {
      setError("Select at least one product variant.");
      return;
    }

    onAddItems(selected);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-white">
      <div className="flex h-full w-full flex-col overflow-hidden bg-white">
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5 sm:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
              <FiFilter size={21} />
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Find Products for Purchase
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Search supplier-linked product variants with current purchase
                price and GST.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200"
            aria-label="Close product search"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6 sm:p-8">
          {error ? (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {isLoadingFilters ? (
            <div className="flex min-h-[280px] items-center justify-center">
              <FiLoader className="animate-spin text-primary-700" size={28} />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <FilterSelect
                  label="Brand"
                  value={filters.brandId}
                  onChange={(value) => updateFilter("brandId", value)}
                  placeholder="All brands"
                  options={filterOptions?.brands || []}
                />

                <FilterSelect
                  label="Category"
                  value={filters.categoryId}
                  onChange={(value) => updateFilter("categoryId", value)}
                  placeholder="All categories"
                  options={filterOptions?.categories || []}
                />

                <FilterSelect
                  label="Subcategory"
                  value={filters.subCategoryId}
                  onChange={(value) => updateFilter("subCategoryId", value)}
                  placeholder="All subcategories"
                  options={subCategories}
                />

                <FilterSelect
                  label="Season"
                  value={filters.seasonId}
                  onChange={(value) => updateFilter("seasonId", value)}
                  placeholder="All seasons"
                  options={filterOptions?.seasons || []}
                />

                <FilterSelect label="Colour" value={filters.colorId} onChange={(value) => updateFilter("colorId", value)} placeholder="All colours" options={filterOptions?.colors || []} />
                <FilterSelect label="Size" value={filters.sizeId} onChange={(value) => updateFilter("sizeId", value)} placeholder="All sizes" options={filterOptions?.sizes || []} />
                <div><label className="mb-2 block text-sm font-medium text-gray-700">GST rule group</label><select value={filters.gstRuleGroup} onChange={(event) => updateFilter("gstRuleGroup", event.target.value)} className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700"><option value="">All GST rules</option>{(filterOptions?.gstRuleGroups || []).map((ruleGroup) => <option key={ruleGroup} value={ruleGroup}>{String(ruleGroup).replace(/_/g, " ")}</option>)}</select></div>

                <div className="md:col-span-2 xl:col-span-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Product, SKU, CHARMS barcode, supplier/EAN barcode, colour, size, or variant
                  </label>

                  <div className="relative">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                    <input
                      value={filters.query}
                      onChange={(event) =>
                        updateFilter("query", event.target.value)
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          searchProducts();
                        }
                      }}
                      placeholder="Search the purchase catalogue..."
                      className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="h-11 rounded-xl border border-gray-200 px-5 text-sm font-medium hover:bg-gray-50"
                >
                  Reset
                </button>

                <button
                  type="button"
                  onClick={searchProducts}
                  disabled={isSearching || !supplierId}
                  className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-sm font-medium text-white disabled:opacity-60"
                >
                  {isSearching ? (
                    <FiLoader className="animate-spin" />
                  ) : (
                    <FiSearch />
                  )}
                  {isSearching ? "Searching..." : "Search Products"}
                </button>
              </div>

              <div className="mt-7 overflow-hidden rounded-2xl border border-gray-100">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-5 py-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      Matching Variants
                    </h3>

                    <p className="mt-1 text-sm text-gray-500">
                      {results.length} result{results.length === 1 ? "" : "s"} ·{" "}
                      {selectedVariantIds.length} selected
                    </p>
                  </div>

                  {results.length ? (
                    <button
                      type="button"
                      onClick={toggleAllResults}
                      className="rounded-xl border border-primary-200 bg-white px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
                    >
                      {allResultsSelected
                        ? "Clear selection"
                        : "Select all results"}
                    </button>
                  ) : null}
                </div>

                {results.length ? (
                  <div className="max-h-[calc(100vh-410px)] overflow-auto">
                    <table className="w-full min-w-[1140px] text-left text-sm">
                      <thead className="sticky top-0 bg-white text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-5 py-4">Select</th>
                          <th className="px-5 py-4">Product</th>
                          <th className="px-5 py-4">SKU</th>
                          <th className="px-5 py-4">CHARMS barcode</th>
                          <th className="px-5 py-4">Supplier / EAN</th>
                          <th className="px-5 py-4">Colour</th>
                          <th className="px-5 py-4">Size</th>
                          <th className="px-5 py-4">GST group</th>
                          <th className="px-5 py-4 text-right">Last cost</th>
                        </tr>
                      </thead>
                      <tbody>
                    {results.map((variant) => {
                      const checked = selectedVariantIds.includes(variant.id);

                      return (
                        <tr
                          key={variant.id}
                          onClick={() => toggleVariant(variant.id)}
                          className={`cursor-pointer border-t border-gray-100 transition hover:bg-primary-50 ${
                            checked ? "bg-primary-50/60" : "bg-white"
                          }`}
                        >
                            <td className="px-5 py-4">
                              <div className={`flex h-6 w-6 items-center justify-center rounded-md border ${
                                checked
                                  ? "border-primary-700 bg-primary-700 text-white"
                                  : "border-gray-300 bg-white"
                              }`}>
                              {checked ? <FiCheck size={15} /> : null}
                              </div>
                            </td>
                            <td className="px-5 py-4 font-semibold text-gray-900">{variant.product?.name}</td>
                            <td className="px-5 py-4 font-mono">{variant.sku}{variant.variantName ? ` · ${variant.variantName}` : ""}</td>
                            <td className="px-5 py-4 font-mono text-xs">{variant.barcode || "—"}</td>
                            <td className="px-5 py-4 font-mono text-xs text-gray-600">{variant.supplierBarcode || "—"}</td>
                            <td className="px-5 py-4">{variant.color?.name || "—"}</td>
                            <td className="px-5 py-4">{variant.size?.name || "—"}</td>
                            <td className="px-5 py-4 text-primary-700">{variant.gstSlab?.ruleGroup ? String(variant.gstSlab.ruleGroup).replace(/_/g, " ") : "—"}</td>
                            <td className="px-5 py-4 text-right font-medium">{money(variant.purchasePrice)}</td>
                        </tr>
                      );
                    })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-6 py-16 text-center text-gray-500">
                    <FiPackage
                      className="mx-auto mb-3 text-gray-300"
                      size={32}
                    />
                    Search products to view supplier-linked variants.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-6 py-5 sm:flex-row sm:justify-end sm:px-8">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-xl border border-gray-200 px-6 font-medium hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={addSelectedProducts}
            disabled={!selectedVariantIds.length}
            className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-700 px-6 font-medium text-white disabled:opacity-50"
          >
            <FiPlus />
            Add {selectedVariantIds.length} Selected Product
            {selectedVariantIds.length === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-700">
        {label}
      </label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700"
      >
        <option value="">{placeholder}</option>

        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
            {option.code ? ` (${option.code})` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
